import {
    EmailTemplateId,
    IContact,
    isPhoneNumberE164,
    parseEntityIdSafe,
    UserId,
    validEmail,
} from "@sp-crm/core";
import { RecipientsField } from "components/messages/recipients-field";
import { UserSelect } from "components/shared/user-select";
import { InlineEmailTemplateSetting } from "components/templates/inline-email-template-setting";
import { AutosavingCheckbox } from "components/ui/autosaving-checkbox";
import { AutosavingInput } from "components/ui/autosaving-input";
import { Checkbox } from "components/ui/checkbox";
import { HtmlInput } from "components/ui/html-input";
import {
    AdvancedSearchEntityType,
    useSetWorkflowSettingMutation,
    WorkflowSettingType,
    WorkflowSettingValue,
} from "generated/graphql";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { loadTenant } from "store/actions";
import { useVisibleUsers } from "store/selectors/hooks";
import {
    getEmailRecipientTokens,
    getSmsRecipientTokens,
    numberSettingValue,
} from "./manage-workflows-helper";
import { SmsBodySetting } from "./sms-body-setting";
import { EditableWorkflow } from "./types";

interface FieldProps {
    workflowSettings: WorkflowSettingValue[];
    setting: EditableWorkflow["settings"][0];
    label: string;
    workflow: EditableWorkflow;
    onSaved: () => void;
    locked: boolean;
}

interface SettingDescriptionProps {
    description: string;
}

const SettingDescription: React.FC<SettingDescriptionProps> = props =>
    props.description ? (
        <div className="ml-6 text-gray-500 group-hover:text-gray-900 text-sm">
            {props.description}
        </div>
    ) : null;

export const WorkflowSettingsField: React.FC<FieldProps> = props => {
    const { workflowSettings, setting, label, workflow, onSaved, locked } = props;
    const users = useVisibleUsers();
    const globalDispatch = useDispatch();

    const mutation = useSetWorkflowSettingMutation();

    const editable =
        !locked && (setting.scope === "user" || !!workflow.regionVisibility?.editable);

    const onSave = React.useCallback(
        async (newValue: string) => {
            await mutation.mutateAsync({
                params: {
                    workflowId: workflow.id,
                    key: setting.key,
                    textValue: newValue,
                },
            });
            loadTenant(globalDispatch);
            onSaved();
        },
        [mutation, workflow.id, setting.key, onSaved, globalDispatch],
    );

    const settingsValue = useMemo(() => {
        return (
            workflowSettings.find(
                s => s.key === setting.key && s.workflowId === workflow.id,
            )?.textValue ?? null
        );
    }, [workflowSettings, setting.key, workflow.id]);

    if (setting.type === WorkflowSettingType.String) {
        return (
            <div className="group">
                <AutosavingInput
                    disabled={!editable}
                    onCommit={newValue => onSave(newValue)}
                    label={label}
                    initial={settingsValue ?? setting.defaultValue ?? ""}
                />
                <SettingDescription description={setting.description} />
            </div>
        );
    }
    if (setting.type === WorkflowSettingType.Number) {
        return (
            <div className="group">
                <AutosavingInput
                    disabled={!editable}
                    onCommit={newValue =>
                        onSave(numberSettingValue(newValue, setting.defaultValue))
                    }
                    label={label}
                    initial={settingsValue ?? setting.defaultValue ?? ""}
                    type="number"
                    step="1"
                    min="0"
                />
                <SettingDescription description={setting.description} />
            </div>
        );
    }
    if (setting.type === WorkflowSettingType.Boolean) {
        return (
            <div className="group">
                <AutosavingCheckbox
                    disabled={!editable}
                    onCommit={newValue => onSave(newValue ? "TRUE" : "FALSE")}
                    label={label}
                    initial={(settingsValue ?? setting.defaultValue ?? "") === "TRUE"}
                />
                <SettingDescription description={setting.description} />
            </div>
        );
    }
    if (setting.type === WorkflowSettingType.EmailTemplateId) {
        return (
            <div className="group">
                <InlineEmailTemplateSetting
                    disabled={!editable}
                    onChange={templateId => onSave(templateId)}
                    value={(settingsValue as EmailTemplateId) || null}
                    bodyLabel="Email body"
                    subjectLabel="Email subject"
                    includeAttachments={true}
                    placeholderTypes={[workflow.attachTo]}
                />
                <SettingDescription description={setting.description} />
            </div>
        );
    }
    if (setting.type === WorkflowSettingType.User) {
        let userValue: string = "";
        if (settingsValue) {
            const userId = parseEntityIdSafe<UserId>(settingsValue);
            if (userId.hasValue && users[userId.get()]) {
                userValue = userId.get();
            }
        }

        return (
            <div>
                <UserSelect
                    disabled={!editable}
                    label={label}
                    includeEveryone={false}
                    includeUnassigned={true}
                    unassignedLabel=""
                    value={userValue as UserId}
                    onChange={userId => onSave(userId)}
                />
            </div>
        );
    }
    if (setting.type === WorkflowSettingType.Html) {
        return (
            <HtmlInput
                label={label}
                initialContent={settingsValue ?? ""}
                onCommit={newValue => onSave(newValue)}
                placeholderTypes={[workflow.attachTo]}
            />
        );
    }

    if (setting.type === WorkflowSettingType.ClientEmails) {
        return (
            <ClientEmailsSetting
                disabled={!editable}
                label={label}
                value={settingsValue ?? setting.defaultValue}
                onChange={newValue => onSave(newValue)}
                attachTo={workflow.attachTo}
            />
        );
    }

    if (setting.type === WorkflowSettingType.ClientPhones) {
        return (
            <ClientPhonesSetting
                disabled={!editable}
                label={label}
                value={settingsValue ?? setting.defaultValue ?? ""}
                onChange={newValue => onSave(newValue)}
                attachTo={workflow.attachTo}
            />
        );
    }

    if (setting.type === WorkflowSettingType.SmsBody) {
        return (
            <SmsBodySetting
                disabled={!editable}
                label={label}
                value={settingsValue ?? setting.defaultValue ?? ""}
                onCommit={newValue => onSave(newValue)}
                placeholderTypes={[workflow.attachTo]}
            />
        );
    }

    const exhaustiveCheck: never = setting.type;
    console.warn(`Unhandled workflow setting type: ${exhaustiveCheck}`);
    return <span />;
};

interface ClientEmailsSettingProps {
    label: string;
    value: string;
    onChange: (newValue: string) => void;
    disabled?: boolean;
    attachTo: AdvancedSearchEntityType;
}

const ClientEmailsSetting: React.FC<ClientEmailsSettingProps> = props => {
    const { label, value, onChange, disabled, attachTo } = props;

    const emails = value.split(",").map(e => e.trim());

    const actualEmailContacts: IContact[] = emails
        .filter(e => validEmail(e))
        .map(e => ({ email1: e }) as IContact);
    const [hasAdditionalEmails, setHasAdditionalEmails] = React.useState(
        actualEmailContacts.length > 0,
    );

    const addToken = (token: string) => {
        onChange(emails.concat(token).join(","));
    };

    const removeToken = (token: string) => {
        onChange(emails.filter(e => e !== token).join(","));
    };

    const removeAdditionalEmails = () => {
        setHasAdditionalEmails(false);
        onChange(emails.filter(e => !validEmail(e)).join(","));
    };

    const setActualEmailContacts = (contacts: IContact[]) => {
        onChange(
            emails
                .filter(e => !validEmail(e))
                .concat(contacts.map(c => c.email1))
                .join(","),
        );
    };

    return (
        <div>
            {label ? <p>{label}</p> : null}
            {getEmailRecipientTokens(attachTo).map(token => (
                <Checkbox
                    disabled={disabled}
                    key={token.token}
                    label={token.label}
                    checked={emails.includes(token.token)}
                    onChange={e => {
                        if (e.target.checked) {
                            addToken(token.token);
                        } else {
                            removeToken(token.token);
                        }
                    }}
                />
            ))}
            <Checkbox
                disabled={disabled}
                label="Additional emails"
                checked={hasAdditionalEmails}
                onChange={e => {
                    if (e.target.checked) {
                        setHasAdditionalEmails(true);
                    } else {
                        removeAdditionalEmails();
                    }
                }}
            />
            {hasAdditionalEmails ? (
                <RecipientsField
                    disabled={disabled}
                    label=""
                    recipients={actualEmailContacts}
                    onChange={setActualEmailContacts}
                    suggestions={[]}
                />
            ) : null}
        </div>
    );
};
interface ClientPhonesSettingProps {
    label: string;
    value: string;
    onChange: (newValue: string) => void;
    disabled: boolean;
    attachTo: AdvancedSearchEntityType;
}

const isLiteralPhoneEntry = (entry: string): boolean => !entry.startsWith("{{");

const ClientPhonesSetting: React.FC<ClientPhonesSettingProps> = props => {
    const { label, value, onChange, disabled, attachTo } = props;

    const entries = value
        .split(",")
        .map(e => e.trim())
        .filter(e => !!e);
    const literalNumbers = entries.filter(isLiteralPhoneEntry);
    const [hasAdditionalNumbers, setHasAdditionalNumbers] = React.useState(
        literalNumbers.length > 0,
    );

    const addToken = (token: string) => {
        onChange(entries.concat(token).join(","));
    };

    const removeToken = (token: string) => {
        onChange(entries.filter(e => e !== token).join(","));
    };

    const removeAdditionalNumbers = () => {
        setHasAdditionalNumbers(false);
        onChange(entries.filter(e => !isLiteralPhoneEntry(e)).join(","));
    };

    const setLiteralNumbers = (raw: string) => {
        onChange(
            entries
                .filter(e => !isLiteralPhoneEntry(e))
                .concat(
                    raw
                        .split(",")
                        .map(n => n.trim())
                        .filter(n => !!n),
                )
                .join(","),
        );
    };

    const invalidNumbers = literalNumbers.filter(n => !isPhoneNumberE164(n));

    return (
        <div>
            {label ? <p>{label}</p> : null}
            {getSmsRecipientTokens(attachTo).map(token => (
                <Checkbox
                    disabled={disabled}
                    key={token.token}
                    label={token.label}
                    checked={entries.includes(token.token)}
                    onChange={e => {
                        if (e.target.checked) {
                            addToken(token.token);
                        } else {
                            removeToken(token.token);
                        }
                    }}
                />
            ))}
            <Checkbox
                disabled={disabled}
                label="Additional phone numbers"
                checked={hasAdditionalNumbers}
                onChange={e => {
                    if (e.target.checked) {
                        setHasAdditionalNumbers(true);
                    } else {
                        removeAdditionalNumbers();
                    }
                }}
            />
            {hasAdditionalNumbers ? (
                <div className="ml-6">
                    <AutosavingInput
                        label=""
                        initial={literalNumbers.join(", ")}
                        onCommit={setLiteralNumbers}
                        disabled={disabled}
                    />
                    <p className="text-sm text-gray-500">
                        Each number receives one text per matching record.
                    </p>
                    {invalidNumbers.length > 0 ? (
                        <p className="text-sm text-red-600">
                            These entries are not valid phone numbers:{" "}
                            {invalidNumbers.join(", ")}
                        </p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
